import { HTMLAttributes, useMemo, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';

import {
  TimeOutsideDuraionTitle,
  TimeOutsideDurationView,
} from '../../vision-profile-views/time-outside-duration-view';
import { ITimeOutsideDurationModalProps } from './time-outside-duration-modal.models';
import { promiseErrorCallbacks } from '../../../../../../utils/promise/set-promise-error-callbacks';
import { VpUpdateModal } from '../../../../../common/vp-update-modal';
import { IFormImperativeHandleProps } from '../../../../../common/form';
import { IVisionProfileData } from '../../../../../../graphql/profile/models/get-vision-profile.models';
import { useCancellablePromise } from '../../../../../../hooks/use-cancellable-promise';
import { useActionsInProgress } from '../../../../../../graphql/preloader/actions/actions-in-progress';
import { GET_VISION_PROFILE } from '../../../../../../graphql/profile/queries/get-vision-profile';
import { UPDATE_VISION_PROFILE } from '../../../../../../graphql/profile/mutations';
import { TTimeOutsideDurationValues } from '../../vision-profile-views/time-outside-duration-view/time-outside-duration-form';

export const TimeOutsideDurationModal = ({
  timeOutsideDuration, onClose, ...props
}: ITimeOutsideDurationModalProps): JSX.Element => {
  const formRef = useRef<IFormImperativeHandleProps>(null);
  const { updateQuery } = useQuery<IVisionProfileData>(GET_VISION_PROFILE);
  const [updateVisionProfile] = useMutation(UPDATE_VISION_PROFILE);
  const { makeCancellablePromise, CancelledPromiseOnUnmountError } = useCancellablePromise();
  const { addActionInProgress, removeActionInProgress } = useActionsInProgress();

  const defaultValues = useMemo(() => ({ timeOutsideDuration }), []);

  const handleSubmit = () => {
    if (formRef.current) {
      formRef.current.submit();
    }
  };

  const handleTimeOutsideDurationChange = async ({
    timeOutsideDuration: timeOutsideDurationCurrent,
  }: TTimeOutsideDurationValues) => {
    addActionInProgress();

    try {
      const { data: { patchVisionProfile } } = await makeCancellablePromise(
        updateVisionProfile({
          variables: {
            visionProfile: {
              timeOutsideDuration: timeOutsideDurationCurrent,
            },
          },
        }),
      );

      updateQuery((prevState) => ({
        visionProfile: {
          ...prevState.visionProfile,
          ...patchVisionProfile,
        },
      }));

      onClose!();
    } catch (error) {
      if (error instanceof CancelledPromiseOnUnmountError) {
        return;
      }

      if (promiseErrorCallbacks.anyError) {
        promiseErrorCallbacks.anyError();
      }
    }

    removeActionInProgress();
  };

  return (
    <VpUpdateModal
      {...props}
      PaperProps={{
        'data-testid': 'vp-time-outside-duration-modal',
      } as HTMLAttributes<HTMLDivElement>}
      title={<TimeOutsideDuraionTitle />}
      content={(
        <TimeOutsideDurationView
          ref={formRef}
          formProps={{
            onSubmit: handleTimeOutsideDurationChange,
            defaultValues,
          }}
        />
      )}
      onVPUpdate={handleSubmit}
      onClose={onClose}
    />
  );
};
